<template>
  <main :style="color">
    <router-view :key="routeKey" @reset-route="resetRoute" />
    <svg-icons />
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SvgIcons from "@/components/SvgIcons";
import IncludeSvg from "@/components/IncludeSvg";
import "@/assets/scss/sm-styles.scss";
import router from "@/router";

import store from "@/store";
import axios from "axios";
import colors from "@/settings/colors";
import roles from "@/settings/roles";

import { GenerateSysColors } from "@/utils/colors";
import { GlobalErrorHandler } from "@/utils/errors";

import Clients from "@/settings/clients";
import { RefreshToken } from "./api/auth";
import Urls from "@/settings/urls";

const checkIsDev = () => {
  return (
    window.location.port == "8080" ||
    window.location.port == "8081" ||
    Clients.devDomains.includes(window.location.hostname)
  );
};

export default {
  name: "app",
  components: {
    SvgIcons,
    IncludeSvg
  },
  data() {
    return {
      resizeListener: "",
      colors: colors,
      axiosInterceptor: null,
      defaultTitle: "Smart Virtual Office | Mailroom Software | Virtual Mailbox",
      defaultFavicon: "/customers/images/favicon.ico",
      routeKey: 0,
    };
  },
  created() {
    this.checkIsMobile();
    this.resizeListener = window.addEventListener("resize", () => this.checkIsMobile());
    this.$on("hook:beforeDestroy", () => {
      window.removeEventListener("resize", this.resizeListener);
    });


    this.setupAxiosInterceptor()

    if (window.location.hostname == "help.spheremail.co") {
      this.$router.push("/tutorial");
      return;
    }

    Promise.all([this.getCountryList(), this.getCurrencies(), this.getColorScheme()]).then(
      ([country_list, currency_list, color_scheme, provider]) => {
        this.checkMarketingIntegrations();
      }
    );

    this.$validator.extend("alpha_latin_dash", {
      getMessage: field => {
        return "The " + field + " field may only contain alphabetic characters and dashes";
      },
      validate: value => {
        const regexp = /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ-]*$/;
        if (value.search(regexp) === -1) return false;
        else return true;
      }
    });

    this.$validator.extend("sm_lower_alpha_num_dash", {
      getMessage: field => {
        return "The " + field + " field may only lowercase characters, numbers and dashes";
      },
      validate: value => {
        const regexp = /^[a-z0-9-]*$/;
        if (value.search(regexp) === -1) return false;
        else return true;
      }
    });
  },
  mounted() {
    window.scrollTo(0, 0);

    this.setFavicon(this.provider, this.vanityDomainCustom);
  },
  destroyed() {
    axios.interceptors.response.eject(this.axiosInterceptor);
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      colorScheme: "general/colorScheme",
      provider: "provider",
      isDev: "isDev",
      providerFeatures: "general/providerFeatures",
      access: "auth/access"
    }),
    color() {
      let res = this.colorScheme.color || this.colorScheme.theme;

      if (this.colors[res]) return this.colors[res];
      else return GenerateSysColors(res);
    },
    vanityDomainCustom() {
      return this.providerFeatures.vanity_domain_custom;
    }
  },
  provide() {
    return {
      resetRoute: this.resetRoute
    };
  },
  methods: {
    ...mapActions({
      checkIsMobile: "checkIsMobile",
      getCountryList: "getCountryList",
      getCurrencies: "getCurrencies",
      getColorScheme: "general/getColorScheme"
    }),
    resetRoute() {
      this.routeKey += 1
    },
    setupAxiosInterceptor() {
      let isRefreshing = false;
      let refreshSubscribers = [];

      const subscribeTokenRefresh = (cb) => {
        refreshSubscribers.push(cb);
      };

      const onRrefreshed = (token) => {
        refreshSubscribers.map((cb) => cb(token));
        refreshSubscribers = [];
      };



      this.axiosInterceptor = axios.interceptors.response.use(
        resp => resp,
        // err => {
        //   GlobalErrorHandler(err, this);
        //   return Promise.reject(err);
        // }
        async (error) => {
          const originalRequest = error.config;



          // // Avoid refreshing token for login page route
          // if (window.location.pathname.includes('/customers/login') || window.location.pathname.includes('/customers/sign-up') || window.location.pathname.includes('/customers/reset-password')) {
          //   GlobalErrorHandler(error, this);
          //   return Promise.reject(error);
          // }

          // Handle unauthorized errors (e.g., token expired)
          if (
            error.response &&
            (error.response.status === 419) &&
            !originalRequest._retry
          ) {
            originalRequest._retry = true;

            if (!isRefreshing) {
              isRefreshing = true;
              try {
                const refresh_token = this.access.refresh_token
                const res = await RefreshToken(refresh_token);
                this.$store.commit("LOGIN_UPDATE", res);

                const newAccessToken = res.access_token
                // const newAccessToken = await refreshAccessToken();

                isRefreshing = false;
                onRrefreshed(newAccessToken);
                axios.defaults.headers.common[
                  "Authorization"
                ] = `Bearer ${newAccessToken}`;
                originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
                return axios(originalRequest);
              } catch (refreshError) {
                console.error("Failed to refresh token:", refreshError);
                isRefreshing = false;
                // localStorage.removeItem("accessToken");
                // localStorage.removeItem("refreshToken");

                this.logOut()

                // localStorage.removeItem("token");
                // sessionStorage.removeItem("is_admin");

                // window.location.href = "/login";

                GlobalErrorHandler(refreshError, this);

                return Promise.reject(refreshError);
              }
            }

            return new Promise((resolve) => {
              subscribeTokenRefresh((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(axios(originalRequest));
              });
            });
          }

          // Handle other errors
          if (error.response) {
            if (error.response.status === 403) {
              console.error("Forbidden - you do not have permission.");
            }
          } else if (error.request) {
            console.error("No response received from server.");
          } else {
            console.error("Error setting up request:", error.message);
          }

          return Promise.reject(error);
        }
      );
    },
    logOut() {
      // this.$router.push('/login');
      const wlp = window.location.port;
      const port = wlp === "8080" || wlp === "8081" ? ":" + wlp : "";
      const http = !port ? "https" : "http";
      const origin = window.location.hostname + port;
      window.location = Urls.logout + `?return_to=${http}://${origin}/customers/login`;
    },

    setFavicon(provider, vanity_domain_custom) {
      let clientPreset = Clients.presets.find(
        item => window.location.hostname.includes(item.slug) || item.id == this.provider.id
      );

      // let savedFavicon = localStorage.getItem("favicon_url");
      // if (!vanity_domain_custom) savedFavicon = null;
      let href;
      let title;

      if (provider.favicon_url && vanity_domain_custom) {
        href = provider.favicon_url;
        // localStorage.setItem("favicon_url", provider.favicon_url);
      }
      // else if (savedFavicon) href = savedFavicon;
      else {
        href = `${window.location.origin}${clientPreset && clientPreset.favicon ? clientPreset.favicon : this.defaultFavicon
          }?v=${Math.floor(Math.random() * 90000) + 10000}`;

        // localStorage.removeItem("favicon_url");
      }

      title = clientPreset && clientPreset.title ? clientPreset.title : this.defaultTitle;

      let link = document.querySelector("link[rel*='icon']") || document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = href;
      document.getElementsByTagName("head")[0].appendChild(link);
      document.title = title;
    },
    checkMarketingIntegrations() {
      this.fbCheckInit();
      this.gaCheckInit();
      this.gadsCheckInit();
      this.hsCheckInit();
    },
    fbCheckInit() {
      if (
        !Object.keys(this.provider).length ||
        !this.provider.facebook_pixel_id ||
        !window.fbq ||
        window.fbq.getState().pixels.length
      )
        return;

      window.fbq("init", this.provider.facebook_pixel_id);

      window.fbq("track", "PageView", window.location.href);
    },
    gaCheckInit() {
      if (
        !Object.keys(this.provider).length ||
        !this.provider.google_analytics_tracking_id ||
        !window.ga ||
        window.gaData
      )
        return;

      window.ga("create", this.provider.google_analytics_tracking_id, "auto");
      window.ga("send", "pageview");
    },
    gadsCheckInit() {
      if (
        !Object.keys(this.provider).length ||
        !this.provider.google_ads_tracking_id ||
        !window.gtag ||
        (window.google_tag_manager && window.google_tag_manager.gtagRegistered)
      )
        return;

      window.gtag("js", new Date());
      window.gtag("config", this.provider.google_ads_tracking_id);
    },
    hsCheckInit() {
      if (!Object.keys(this.provider).length || !this.provider.hubspot_tracking_id) return;

      // TEMP
      // this.provider.hubspot_tracking_id = '8730385';

      let script = document.createElement("script");
      script.id = "hs-script-loader";
      script.async = true;
      script.defer = true;
      script.src = `//js.hs-scripts.com/${this.provider.hubspot_tracking_id}.js`;
      script.onload = e => {
        let _hsq = (window._hsq = window._hsq || []);
        _hsq.push(["setPath", window.location.href]);
        _hsq.push(["trackPageView"]);
      };
      script.onerror = e => {
        console.log("error hubspot", e);
      };

      if (!document.getElementById("hs-script-loader")) {
        document.head.appendChild(script);
      }
    }
  },
  watch: {
    provider(val) {
      this.checkMarketingIntegrations();
      // this.setFavicon(val);
    },
    vanityDomainCustom(val) {
      this.setFavicon(this.provider, val);
    },
    $route(to, from) {
      if (window && window.fbq && this.provider && this.provider.facebook_pixel_id)
        window.fbq("track", "PageView", window.location.href);

      if (window && this.provider && this.provider.hubspot_tracking_id) {
        let _hsq = (window._hsq = window._hsq || []);
        _hsq.push(["setPath", window.location.href]);
        _hsq.push(["trackPageView"]);
      }
    }
  }
};

router.beforeEach((to, from, next) => {
  if (to.query.refresh_token && to.query.access_token) {
    localStorage.setItem("token", to.query.refresh_token);

    store.commit("auth/LOGIN_SUCCESS", {
      access_token: to.query.access_token,
      refresh_token: to.query.refresh_token,
      account: {},
      expire_at: ""
    });
    axios.defaults.headers.common.Authorization = `Bearer ${to.query.access_token}`;

    if (to.query.is_admin) sessionStorage.setItem("is_admin", 1);
    else sessionStorage.removeItem("is_admin");
  }

  if (!!to.meta.forAuth) {
    // for single sign on
    const origin = window.location.hostname;
    if (from.query.uni_sso) {
      const api = checkIsDev() ? "staging-api" : "api";
      window.location.href =
        `https://${api}.spheremail.co/auth/auth0/connect?origin=https://` + origin;
    } else {
      store
        .dispatch("CheckAuth")
        .then(acc => {
          acc.role = acc.privileged_user ? "admin" : "customer";

          if (acc.role == roles.customer && sessionStorage.getItem("is_admin")) {
            store.dispatch("setIsAdmin", true);
          }

          let redirect = getRoleRedirect(acc, to);
          if (redirect) {
            next(redirect);
          } else if (
            // Customer has no access to mail
            acc.level != 1 &&
            !acc.privileged_user &&
            (!acc.can_access_mail_manager ||
              (acc.type == "AdditionalRecipient" && acc.additional_recipient_status == "inactive")) &&
            to.name.includes("mail")
          ) {
            next("/account");
          } else {
            next();
          }
        })
        .catch(() =>{
          if(!window.location.hostname.includes("integrations")){
            if(window.location.hostname == "help.spheremail.co"){
              next("/tutorial")
            } else {
              next("/login")
            }
          }
        }
        );
    }
  } else if(to.name != "connect" || to.name != "configure") {
    next();
  }
});

const getRoleRedirect = (acc, to) => {
  if (acc.type == 'AdditionalRecipient') return false
  if (to.name == "app") return `/${acc.role}`;

  if (to.meta.roles && !to.meta.roles.includes(acc.role))
    // Role permissions
    return "/";

  if (acc.role == roles.customer) {
    if ((!acc.activated_on || !acc.activated_on.length) && acc.level != 1) {
      // if customer not activated and not lvl1
      if (acc.plan_id)
        // if selected plan
        return "/billing-info";
      // if not selected plan
      else return "/select-plan";
    }
  }

  if (acc.role == roles.admin) {
    const NO_REDIRECT = ["admin-settings", "admin-account", "redirect"];

    if (
      acc.hasOwnProperty("client_access_allowed") &&
      !acc.client_access_allowed &&
      !NO_REDIRECT.includes(to.name)
    ) {
      // No credit card
      return "settings?tab=payments";
    }
  }

  return false;
};
</script>
